import './tiers.css'
import { useI18n } from '../lib/i18n'
import { Reveal } from '../components/Reveal'
import { SectionHeader } from '../components/SectionHeader'
import { HudCard } from '../components/HudCard'
import { Button } from '../components/Button'

// 04 · SẢN PHẨM — three commercialization tiers. Tier 1 (EUDR Ready Pack) is
// the featured leaf-bordered card; leaf is the signature mark so a subtle leaf
// glow is sanctioned here only (taste rule §1).
const TIER_BG: Record<string, string> = { '01': 'aerial', '02': 'drying', '03': 'drone' }

export default function Tiers() {
  const { lang } = useI18n()
  const C = (
    {
      vi: {
        kicker: 'SẢN PHẨM',
        title: (
          <>
            Ba tầng, <em>thương mại hóa theo thứ tự</em>.
          </>
        ),
        sub: 'Bắt đầu với bộ hồ sơ tuân thủ cho mỗi lô, rồi mở rộng sang nhật ký mùa vụ và giám sát thực địa liên tục khi vùng trồng lớn dần.',
        recommended: 'Khuyến nghị',
        tiers: [
          {
            id: '01',
            name: 'EUDR Ready Pack',
            tagline: 'Bộ hồ sơ DDS-ready cho từng lô hàng.',
            featured: true,
            features: [
              'Lot Passport + GeoJSON cho TRACES NT',
              'Verdict mỗi lô: Đạt / Cần rà soát / Không đạt',
              'Mốc cắt 31/12/2020 đối chiếu Sentinel-2 · JRC TMF',
              'Sẵn cho nhà nhập khẩu EU nộp DDS',
            ],
            cta: 'Tạo Lot Passport',
          },
          {
            id: '02',
            name: 'Lot Journal',
            tagline: 'Nhật ký lô và cập nhật theo mùa vụ.',
            featured: false,
            features: [
              'Lịch sử ranh giới và diện tích từng lô',
              'Cập nhật trạng thái theo mùa vụ',
              'Đối chiếu GFC loss · GFW · Whisp',
              'Xuất hồ sơ tổng hợp theo vùng trồng',
            ],
            cta: 'Mở nhật ký lô',
          },
          {
            id: '03',
            name: 'Live Monitoring',
            tagline: 'Giám sát thực địa qua webhook hardware.',
            featured: false,
            features: [
              'Webhook hardware → bằng chứng liên tục',
              'Cảnh báo sớm khi phát hiện mất rừng',
              'Bằng chứng nối chuỗi cho mỗi lô',
              'Hồ sơ luôn cập nhật cho kỳ kiểm tra',
            ],
            cta: 'Kích hoạt giám sát',
          },
        ],
      },
      en: {
        kicker: 'PRODUCTS',
        title: (
          <>
            Three tiers, <em>commercialized in order</em>.
          </>
        ),
        sub: 'Start with the per-lot compliance pack, then expand into seasonal journals and continuous field monitoring as your growing area scales.',
        recommended: 'Recommended',
        tiers: [
          {
            id: '01',
            name: 'EUDR Ready Pack',
            tagline: 'A DDS-ready dossier for every lot.',
            featured: true,
            features: [
              'Lot Passport + GeoJSON for TRACES NT',
              'Per-lot verdict: Pass / Review / Fail',
              '2020-12-31 cutoff checked vs Sentinel-2 · JRC TMF',
              'Ready for the EU importer to file a DDS',
            ],
            cta: 'Generate Lot Passport',
          },
          {
            id: '02',
            name: 'Lot Journal',
            tagline: 'Per-lot journal with seasonal updates.',
            featured: false,
            features: [
              'Boundary and area history per lot',
              'Status updates across the season',
              'Cross-checked vs GFC loss · GFW · Whisp',
              'Export consolidated dossiers by region',
            ],
            cta: 'Open lot journal',
          },
          {
            id: '03',
            name: 'Live Monitoring',
            tagline: 'Field monitoring via hardware webhooks.',
            featured: false,
            features: [
              'Hardware webhook → continuous evidence',
              'Early alerts when loss is detected',
              'Chained evidence for every lot',
              'Always-current dossier for audits',
            ],
            cta: 'Enable monitoring',
          },
        ],
      },
    } as const
  )[lang]

  return (
    <section id="products" className="section tiers">
      <div className="container">
        <SectionHeader index="05" kicker={C.kicker} title={C.title} sub={C.sub} />

        <div className="tiers-grid">
          {C.tiers.map((tier, i) => (
            <Reveal key={tier.id} delay={0.06 * i} y={24}>
              <HudCard
                className={`tiers-card${tier.featured ? ' tiers-card--featured' : ''}`}
                header={
                  <span className="tiers-card-head">
                    <span className="tiers-id">{`TIER ${tier.id}`}</span>
                    {tier.featured && <span className="label tiers-recommended">{C.recommended}</span>}
                  </span>
                }
                brackets
              >
                <img className="tiers-bg" src={`/img/${TIER_BG[tier.id] ?? 'aerial'}.jpg`} alt="" aria-hidden loading="lazy" decoding="async" />
                <div className="tiers-body">
                  <h3 className="tiers-name">{tier.name}</h3>
                  <p className="tiers-tagline">{tier.tagline}</p>

                  <ul className="tiers-features">
                    {tier.features.map((f) => (
                      <li key={f} className="tiers-feature">
                        <svg
                          className="tiers-check"
                          viewBox="0 0 16 16"
                          width="16"
                          height="16"
                          aria-hidden="true"
                        >
                          <path
                            d="M3.5 8.5l3 3 6-7"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="tiers-cta">
                    <Button variant="ghost" href="/app">
                      {tier.cta}
                    </Button>
                  </div>
                </div>
              </HudCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}
